/**
 * Access requests — the PURE half: row model and HTML, no Firebase.
 *
 * Split from access-requests.ts (2026-09-21) so it can be tested:
 * importing the panel pulls in cf-client → firebase.ts, which reads
 * build-time constants a test run does not define. Same shape as
 * add-user-rules.ts beside add-user.ts.
 */
import { escapeHtml } from "../utils/html";

export interface AccessRequestRow {
  id: string;
  verifiedPhone?: string | null;
  status?: string | null;
  requestedAt?: string | null;
  supplied: {
    name?: string | null;
    client?: string | null;
    relationship?: string | null;
  };
  officeNotified?: boolean;
  /**
   * ADR-0045 amendment 2026-09-21: contacts whose on-file number matched but
   * whom the office had not granted. From that date a match is not access —
   * the person lands here, already identified, and the office's yes is one
   * click. Empty when the number matched nobody.
   */
  matchedRelatedPersonDocIds?: string[];
}

/** Whether the office can grant straight from the queue (a contact matched). */
export function canGrantFromQueue(row: AccessRequestRow): boolean {
  return (row.matchedRelatedPersonDocIds ?? []).length > 0;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "unknown";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleString("en-GB");
}

/** Unverified free text the requester typed — never presented as fact. */
function suppliedLine(row: AccessRequestRow): string {
  const parts: string[] = [];
  parts.push(`“${escapeHtml(row.supplied?.name || "no name given")}”`);
  if (row.supplied?.client) parts.push(`client “${escapeHtml(row.supplied.client)}”`);
  if (row.supplied?.relationship) parts.push(`“${escapeHtml(row.supplied.relationship)}”`);
  return parts.join(" · ");
}

export function renderRequestRow(row: AccessRequestRow): string {
  return `
    <div class="admin-row" data-request-id="${escapeHtml(row.id)}">
      <div style="flex:1;min-width:0;">
        <div style="font-size:15px;font-weight:600;">
          📱 ${escapeHtml(row.verifiedPhone || "unknown number")}
          <span style="font-size:11px;font-weight:600;color:#15803d;">✓ verified by one-time code</span>
        </div>
        <div style="font-size:13px;color:#6b7280;margin-top:2px;">
          They typed <em>(unverified — check against records)</em>: ${suppliedLine(row)}
        </div>
        <div style="font-size:13px;color:#6b7280;margin-top:2px;">
          requested ${escapeHtml(formatWhen(row.requestedAt))}
          ${row.officeNotified === false
            ? ' · <span style="color:#b45309;font-weight:600;">⚠ office was not emailed</span>'
            : ""}
        </div>
        <div style="font-size:13px;margin-top:2px;">
          ${canGrantFromQueue(row)
            ? `<span style="color:#15803d;font-weight:600;">✓ This number belongs to a contact on file</span>
               — giving access lets them in straight away.`
            : `<span style="color:#6b7280;">No contact on file has this number.</span>
               Add them to the client's contacts in OneTouch, then give access from that contact.`}
        </div>
      </div>
      <div style="display:flex;gap:8px;align-items:center;">
        <button class="btn-secondary" data-request-action="rejected">Reject</button>
        ${canGrantFromQueue(row)
          ? `<button class="btn-primary" data-request-grant>Give access</button>`
          : `<button class="btn-primary" data-request-action="resolved">Mark handled</button>`}
      </div>
    </div>`;
}

export function renderRequestList(rows: AccessRequestRow[]): string {
  if (rows.length === 0) {
    return `<div class="admin-empty">No pending access requests.</div>`;
  }
  return rows.map(renderRequestRow).join("");
}
